import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessengerChatShell } from '../components/messaging/MessengerChatShell';
import { MessengerComposer } from '../components/messaging/MessengerComposer';
import { MessengerMessageActionSheet } from '../components/messaging/MessengerMessageActionSheet';
import { MessengerMessageBubble } from '../components/messaging/MessengerMessageBubble';
import { MissedCallChip } from '../components/messaging/MissedCallChip';
import { MessengerMessageInfoSheet } from '../components/messaging/MessengerMessageInfoSheet';
import { MessengerPinnedStrip } from '../components/messaging/MessengerPinnedStrip';
import NotificationBellButton from '../components/NotificationBellButton';
import { useCall } from '../contexts/CallContext';
import { getMessengerGroupMeta } from '../components/messaging/messengerUtils';
import { buildReplyPayload, copyTextToClipboard, filterVisibleMessages, formatSeenTimestamp, getPinnedMessage, resolveOwnStatusVariant, summarizeMessageText } from '../components/messaging/messengerThreadUtils';
import { formatChatDisplayName } from '../utils/nameFormatters';
import { buildMessageHeaders } from '../utils/messageHeaders';
import { fetchAccountSettings } from '../utils/accountSettingsApi';
import { primeCallPermissions } from '../utils/callPermissions';
import { SupportClosedBanner, renderSupportSystemMessage } from '../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatConversationPage() {
  const { convoId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [infoTarget, setInfoTarget] = useState(null);
  const [myAllowCalls, setMyAllowCalls] = useState(true);
  const { startVoiceCall } = useCall();

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    // Prime mic permission the FIRST time the user opens ANY chat, not
    // just the inbox — otherwise a deep-link straight into a conversation
    // (push notification, share link, etc.) lets them tap the phone
    // button before the OS mic prompt has ever fired, which makes the
    // WebRTC call silently fail with "Unable to start the call".
    primeCallPermissions('guest');
    fetchAccountSettings()
      .then((settings) => setMyAllowCalls(settings.privacy?.allow_phone_calls !== false))
      .catch(() => {});
  }, []);

  const logGuestChatError = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  const fetchConversation = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations/${convoId}`, {
        headers: buildMessageHeaders(token, null, 'guest'),
      });
      setMessages(response.data.messages || []);
      setConversation(response.data.conversation || null);
      setOtherUser(response.data.other_user || null);
    } catch (error) {
      logGuestChatError('Failed to load guest chat', error);
      toast.error(error.response?.data?.detail || 'Failed to load this conversation.');
    } finally {
      setLoading(false);
    }
  }, [convoId, logGuestChatError]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Live-refresh the open chat when a new message lands via Socket.IO.
  // The backend emits `conversation_message_received` to the recipient's
  // personal room on every send — so even if the recipient already has
  // this chat open in another tab, messages appear in real time without
  // needing to navigate away and back.
  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.conversation_id && event.detail.conversation_id !== convoId) return;
      fetchConversation();
    };
    window.addEventListener('allure:conversation-message-received', handler);
    return () => window.removeEventListener('allure:conversation-message-received', handler);
  }, [convoId, fetchConversation]);

  // Patch tick state in place when the other side acks delivered / read.
  // Avoids a full refetch — bubbles flip instantly.
  useEffect(() => {
    const applyStatus = (kind) => (event) => {
      const detail = event?.detail || {};
      if (detail.conversation_id && detail.conversation_id !== convoId) return;
      const ids = new Set(detail.message_ids || []);
      if (!ids.size) return;
      setMessages((prev) => prev.map((message) => {
        if (!ids.has(message.id)) return message;
        if (kind === 'delivered') return { ...message, delivered: true, delivered_at: detail.at || message.delivered_at };
        return { ...message, read: true, read_at: detail.at || message.read_at, delivered: true, delivered_at: message.delivered_at || detail.at };
      }));
    };
    const delivered = applyStatus('delivered');
    const read = applyStatus('read');
    window.addEventListener('allure:conversation-messages-delivered', delivered);
    window.addEventListener('allure:conversation-messages-read', read);
    return () => {
      window.removeEventListener('allure:conversation-messages-delivered', delivered);
      window.removeEventListener('allure:conversation-messages-read', read);
    };
  }, [convoId]);

  const visibleMessages = useMemo(() => filterVisibleMessages(messages), [messages]);
  const isSupportConvo = conversation?.type === 'support';
  const supportClosed = isSupportConvo && conversation?.support_status === 'closed';
  // Support threads always present as "Allure" + Allure logo in the top bar
  // and inside message bubbles, regardless of which admin actually typed
  // the reply. Guests should see one consistent "Allure" identity.
  const SUPPORT_CONTACT_NAME = 'Allure';
  const SUPPORT_CONTACT_IMAGE = '/notification-icon-192.png';
  const contactName = isSupportConvo ? SUPPORT_CONTACT_NAME : (formatChatDisplayName(otherUser) || 'Chat');
  const contactImage = isSupportConvo ? SUPPORT_CONTACT_IMAGE : (otherUser?.image_url || otherUser?.photo_url);
  const pinnedMessage = useMemo(() => getPinnedMessage(visibleMessages), [visibleMessages]);
  // Server-side verdict: conversation-scoped guest↔host pair + both privacy
  // toggles ON. Never derived from `otherUser.role` (mutable on role switch).
  const showCallButton = useMemo(() => {
    if (!conversation?.can_call) return false;
    if (otherUser?.allow_phone_calls === false) return false;
    return myAllowCalls;
  }, [conversation?.can_call, otherUser?.allow_phone_calls, myAllowCalls]);
  // Red dot on the phone icon when this thread has an unread missed-call
  // event addressed to the current viewer. Filters to `no_answer|missed`
  // only — a `declined` chip (recipient = caller) doesn't warrant a
  // badge because the caller already knows they were declined.
  const hasMissedCall = useMemo(() => visibleMessages.some((m) => (
    m.message_type === 'call_event'
    && m.recipient_id === user.id
    && !m.read
    && ['no_answer', 'missed'].includes((m.metadata || {}).status)
  )), [visibleMessages, user.id]);
  const latestOutgoingMessageId = useMemo(() => [...visibleMessages].reverse().find((message) => message.sender_id === user.id)?.id || null, [user.id, visibleMessages]);
  const buildStatusText = useCallback((message) => {
    if (!message?.read) return '';
    const seenTime = formatSeenTimestamp(message?.read_at);
    return seenTime ? `Seen · ${seenTime}` : '';
  }, []);

  const sendMessage = useCallback(async ({ content = '', messageType = 'text', metadata = {}, previewText = '' }) => {
    try {
      const token = localStorage.getItem('token');
      const enrichedMetadata = replyingTo ? { ...metadata, reply_to: replyingTo } : metadata;
      const response = await axios.post(`${API}/messages/send`, {
        conversation_id: convoId,
        content,
        message_type: messageType,
        metadata: enrichedMetadata,
        preview_text: previewText,
      }, {
        headers: buildMessageHeaders(token, null, 'guest'),
      });
      setMessages((prev) => [...prev, response.data]);
      setReplyingTo(null);
    } catch (error) {
      logGuestChatError('Failed to send guest message', error);
      toast.error(error.response?.data?.detail || 'Failed to send the message.');
      throw error;
    }
  }, [convoId, logGuestChatError, replyingTo]);

  const handleSendText = useCallback(async (text) => {
    await sendMessage({ content: text, messageType: 'text', previewText: text });
    setNewMessage('');
  }, [sendMessage]);

  const handleSendImage = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'image', metadata, previewText: metadata.source === 'camera' ? 'Sent a photo' : 'Sent an image' });
  }, [sendMessage]);

  const handleSendVideo = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'video', metadata, previewText: metadata.source === 'camera' ? 'Sent a video' : 'Sent a video' });
  }, [sendMessage]);

  const handleSendAudio = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'audio', metadata, previewText: 'Sent a voice message' });
  }, [sendMessage]);

  const composer = supportClosed ? (
    <SupportClosedBanner
      onOpenNewTicket={() => navigate('/support/contact')}
      dataTestId="guest-chat-support-closed-banner"
    />
  ) : (
    <MessengerComposer
      value={newMessage}
      onChange={(event) => setNewMessage(event.target.value)}
      onSendText={handleSendText}
      onSendImage={handleSendImage}
      onSendVideo={handleSendVideo}
      onSendAudio={handleSendAudio}
      replyingTo={replyingTo}
      onCancelReply={() => setReplyingTo(null)}
      editingMessage={editingMessage}
      onCancelEdit={() => { setEditingMessage(null); setNewMessage(''); }}
      onSaveEdit={async (message, text) => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/messages/${message.id}/edit`, { content: text }, { headers: { Authorization: `Bearer ${token}` } });
          setMessages((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
          setEditingMessage(null);
          setNewMessage('');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to edit the message.');
        }
      }}
      placeholder="Message"
      dataTestIdPrefix="guest-chat"
    />
  );

  const applyUpdatedMessage = useCallback((updatedMessage) => {
    setMessages((prev) => prev.map((message) => (message.id === updatedMessage.id ? updatedMessage : message)));
  }, []);

  const reactToMessage = useCallback(async (message, emoji, silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const currentReaction = message?.metadata?.reactions?.[user.id] || '';
      const nextEmoji = currentReaction === emoji ? '' : emoji;
      const response = await axios.post(`${API}/messages/${message.id}/reaction`, { emoji: nextEmoji }, { headers: { Authorization: `Bearer ${token}` } });
      applyUpdatedMessage(response.data);
      setActionTarget(null);
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Failed to react to the message.');
      }
    }
  }, [applyUpdatedMessage, user.id]);

  const copyMessage = useCallback(async (message) => {
    const copied = await copyTextToClipboard(summarizeMessageText(message));
    if (copied) {
      toast.success('Copied');
    } else {
      toast.error('Unable to copy this message.');
    }
    setActionTarget(null);
  }, []);

  const pinMessage = useCallback(async (message, pinned = true) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages/${message.id}/pin`, { pinned }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => prev.map((item) => {
        if (item.conversation_id !== message.conversation_id) return item;
        if (item.id === response.data.id) return response.data;
        return { ...item, metadata: { ...(item.metadata || {}), pinned: false } };
      }));
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin the message.');
    }
  }, []);

  const unsendMessage = useCallback(async (message) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${message.id}/unsend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => prev.filter((item) => item.id !== message.id));
      if (replyingTo?.id === message.id) setReplyingTo(null);
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsend the message.');
    }
  }, [replyingTo?.id]);

  return (
    <>
      <MessengerChatShell
        pageTestId="guest-chat-page"
        bellNode={<NotificationBellButton buttonTestId="guest-chat-bell-button" popupTestId="guest-chat-notification-popup" />}
        onBack={() => navigate('/guest/share')}
        backTestId="guest-chat-back-button"
        contactName={contactName}
        contactImage={contactImage}
        contactTestId="guest-chat-contact-avatar"
        showCallButton={showCallButton}
        onCall={() => startVoiceCall({ conversationId: convoId, contact: otherUser })}
        callTestId="guest-chat-phone-button"
        callBadge={hasMissedCall && showCallButton}
        messages={visibleMessages}
        loading={loading}
        emptyText="No messages yet. Say hello!"
        composer={composer}
        pinnedNode={pinnedMessage ? <MessengerPinnedStrip message={pinnedMessage} previewText={summarizeMessageText(pinnedMessage)} onClear={() => pinMessage(pinnedMessage, false)} /> : null}
        scrollDependency={visibleMessages.length}
        renderMessage={(message, index) => {
          const isMine = message.sender_id === user.id;
          const messageKey = `${message.id}-${message.created_at || message.sender_id || 'message'}`;
          // Support system messages (Yes/No card, thanks, declined) render
          // as their own cards instead of regular bubbles.
          const supportRender = renderSupportSystemMessage(message, {
            conversation,
            isMine,
            onResolved: () => fetchConversation(),
            dataTestId: `guest-chat-support-${messageKey}`,
          });
          if (supportRender) return supportRender.node;
          if (message.message_type === 'call_event' && showCallButton) {
            return (
              <MissedCallChip
                key={messageKey}
                message={message}
                viewerId={user.id}
                canCallBack={showCallButton}
                onCallBack={() => startVoiceCall({ conversationId: convoId, contact: otherUser })}
                dataTestId={`guest-chat-missed-call-${messageKey}`}
              />
            );
          }
          if (message.message_type === 'call_event') {
            // Guest lost the ability to call this partner (privacy toggle
            // flipped off after the missed-call event was written). Show
            // the chip without a callback CTA — copy still gives context.
            return (
              <MissedCallChip
                key={messageKey}
                message={message}
                viewerId={user.id}
                canCallBack={false}
                dataTestId={`guest-chat-missed-call-${messageKey}`}
              />
            );
          }
          const groupMeta = getMessengerGroupMeta(visibleMessages, index, (candidate) => (candidate.sender_id === user.id ? 'me' : 'other'));
          return (
            <MessengerMessageBubble
              key={messageKey}
              message={message}
              isMine={isMine}
              avatarName={isMine ? (user.name || 'You') : contactName}
              avatarImage={isMine ? (user.photo_url || user.image_url) : contactImage}
              dataTestId={`guest-chat-message-${messageKey}`}
              showAvatar={!groupMeta.nextSameSender}
              groupPosition={groupMeta.groupPosition}
              spacingMode="guest"
              statusText={isMine && message.id === latestOutgoingMessageId ? buildStatusText(message) : ''}
              statusVariant={isMine ? resolveOwnStatusVariant(message) : null}
              onReply={(targetMessage, mine) => setReplyingTo(buildReplyPayload(targetMessage, mine ? (user.name || 'You') : contactName))}
              onLongPress={(targetMessage, mine) => setActionTarget({ message: targetMessage, isMine: mine })}
              onDoubleTapHeart={(targetMessage) => reactToMessage(targetMessage, '❤️', true)}
            />
          );
        }}
      />
      <MessengerMessageActionSheet
        open={Boolean(actionTarget)}
        message={actionTarget?.message}
        isMine={Boolean(actionTarget?.isMine)}
        onClose={() => setActionTarget(null)}
        onReact={(emoji) => reactToMessage(actionTarget.message, emoji)}
        onCopy={(message) => copyMessage(message)}
        onInfo={(message) => { setInfoTarget({ message, isMine: Boolean(actionTarget?.isMine) }); setActionTarget(null); }}
        onPin={(message) => pinMessage(message)}
        onReply={(message) => { setReplyingTo(buildReplyPayload(message, actionTarget?.isMine ? (user.name || 'You') : contactName)); setActionTarget(null); }}
        onEdit={(message) => { setEditingMessage(message); setNewMessage(message.content || ''); setReplyingTo(null); setActionTarget(null); }}
        onUnsend={(message) => unsendMessage(message)}
      />
      <MessengerMessageInfoSheet
        open={Boolean(infoTarget)}
        message={infoTarget?.message}
        isMine={Boolean(infoTarget?.isMine)}
        currentUser={{ id: user.id, name: user.name || 'You', image_url: user.photo_url || user.image_url }}
        otherUser={otherUser}
        onClose={() => setInfoTarget(null)}
        dataTestId="guest-message-info-sheet"
      />
    </>
  );
}
